import { useEffect, useRef, useState } from "react"
import ProfileImage from "../../../assets/images/profileIcon.svg"
import { EmployeeTableData } from "../modal/EmployeesContext"
import Box from "../../../shared/Box";
import useIntersectionObserver from "../../../shared/UseIntersectionObserver";
import Button from "../../../shared/Button";
import { useEmployees } from "../modal/EmployeesContext";
import { useNavigate } from "react-router-dom";
import StatusModal from "./StatusModal";
import DeleteModal from "shared/DeleteModal";
import Modal from "shared/Modal";
import Spinner from "shared/Spinner";
import RoleModal from "features/roles/ui/RoleModal";
import { Users, KeyRound, LogIn, History as HistoryIcon, UserX } from "lucide-react";
import { useContext } from "react";
import { VerifyContext } from "app/VerifyContext";
import { resetEmployeePassword } from "../api/employeesApi";
import RowActionsMenu from "shared/RowActionsMenu";


const EmployeeTable = () => {
    const { employeesList, availableRoles, updateStatus, handleEmployeeDelete, isEmployeeDelete, setIsEmployeeDelete, openRoleModal, selectedEmployeeForRole, isRoleModalOpen, closeRoleModal } = useEmployees();
    const { superAdmin, impersonateAs } = useContext(VerifyContext);
    const navigate = useNavigate();
    const [currentEmployee, setCurrentEmployee] = useState<EmployeeTableData | null>(null)
    const [impersonatingCode, setImpersonatingCode] = useState<string | null>(null)

    const [resetTarget, setResetTarget] = useState<EmployeeTableData | null>(null);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [resetError, setResetError] = useState("");
    const [resetSaving, setResetSaving] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const resetModalRef = useRef<HTMLDivElement>(null);

    const openResetModal = (employee: EmployeeTableData) => {
        setResetTarget(employee);
        setNewPassword("");
        setConfirmPassword("");
        setResetError("");
        setResetSuccess(false);
        window.scrollTo(0, 0);
        document.body.style.overflow = "hidden";
    };

    const closeResetModal = () => {
        setResetTarget(null);
        setNewPassword("");
        setConfirmPassword("");
        setResetError("");
        setResetSuccess(false);
        document.body.style.overflow = "auto";
    };

    const handleResetPassword = async () => {
        if (newPassword.length < 8) {
            setResetError("Password must be at least 8 characters");
            return;
        }
        if (newPassword !== confirmPassword) {
            setResetError("Passwords do not match");
            return;
        }
        setResetSaving(true);
        setResetError("");
        try {
            await resetEmployeePassword(resetTarget!.id!, newPassword);
            setResetSuccess(true);
        } catch (e: any) {
            setResetError(e.response?.data?.detail || "Failed to reset password");
        } finally {
            setResetSaving(false);
        }
    };

    const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 }) as [
        React.RefObject<HTMLDivElement>,
        boolean
    ];
    const deleteModalRef = useRef<HTMLDivElement>(null);

    const [hasAnimated, setHasAnimated] = useState<boolean>(false);

    useEffect(() => {
        if (isVisible && !hasAnimated) {
            setHasAnimated(true);
        }
    }, [isVisible, hasAnimated]);




    const handleNameClick = (employee: EmployeeTableData) => {
        navigate(`/admin/employees/update-employees/${employee.id}`);
    };

    const handleHistoryClick = (employee: EmployeeTableData) => {
        navigate(`/admin/employees/increament-history/${employee.id}`)
    }

    const statusModalOpen = (employee: EmployeeTableData) => {
        setCurrentEmployee(employee)
        window.scrollTo(0, 0);
        document.body.style.overflow = "hidden"
    }

    const updateStatusText = (id: string, newStatus: string) => {
        updateStatus(id, newStatus)
        setCurrentEmployee(null)
        window.scrollTo(0, 0);
        document.body.style.overflow = "auto"
    }
    const closeStatusModal = () => {
        setCurrentEmployee(null)
        window.scrollTo(0, 0);
        document.body.style.overflow = "auto"
    }
    const handleImpersonate = async (employee: EmployeeTableData) => {
        if (!employee.id || impersonatingCode) return;
        setImpersonatingCode(employee.id);
        try {
            await impersonateAs(employee.id);
            navigate("/");
        } catch {
            setImpersonatingCode(null);
        }
    };

    const deleteEmployee = (employee: EmployeeTableData) => {
        setIsEmployeeDelete(employee)
        window.scrollTo(0, 0);
        document.body.style.overflow = "hidden"
    }

    const deleteModalClose = () => {
        setIsEmployeeDelete(null)
        window.scrollTo(0, 0);
        document.body.style.overflow = "auto"
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                deleteModalRef.current &&
                !deleteModalRef.current.contains(event.target as Node)
            ) {
                deleteModalClose()
            }
            if (
                resetModalRef.current &&
                !resetModalRef.current.contains(event.target as Node)
            ) {
                closeResetModal();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <>
            <Box ref={ref}
                boxMainDivClasses={` mt-[30px] transition-all duration-500 delay-300 ${hasAnimated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                    }`}
            >
                <div className="w-full overflowXAuto">
                    <table className="w-full">
                        <thead className="bg-surface-muted">
                            <tr className="border-b border-border">
                                <th className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter w-[30%] pl-6">
                                    Employee
                                </th>
                                <th className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter w-[20%]">
                                    Designation
                                </th>
                                <th className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter w-[20%]">
                                    Status
                                </th>
                                <th className="py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter w-[20%]">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeesList.map((data: EmployeeTableData, index: number) => (
                                <tr key={index} className="border-t border-solid border-border"
                                >
                                    <td className="py-4 px-4 w-[30%] flex items-center gap-4 md:gap-[29px]">
                                        <img src={ProfileImage} alt="Profile" className="sm:w-10 sm:h-10 w-[30px] h-[30px]" />
                                        <div className="flex flex-col items-start">
                                            <button onClick={() => handleNameClick(data)} className="text-sm font-inter text-foreground whitespace-nowrap">
                                                {data.name}
                                            </button>
                                            {data.id && (
                                                <span className="text-xs text-muted-foreground font-inter">{data.id}</span>
                                            )}
                                            {data.email && (
                                                <span className="text-xs text-muted-foreground font-inter">{data.email}</span>
                                            )}
                                            {data.secondaryEmail && (
                                                <span className="text-xs text-muted-foreground font-inter">
                                                    <span className="text-muted-foreground">Secondary:</span> {data.secondaryEmail}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="w-[20%] py-4 px-4 text-sm text-foreground font-inter">
                                        {data.designation || <span className="text-muted-foreground">—</span>}
                                        {(data.role_ids || []).length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {(data.role_ids || []).map(id => {
                                                    const role = availableRoles.find(r => r.id === id);
                                                    return role ? (
                                                        <span key={id} className="text-xs text-primary font-inter">
                                                            {role.role_name}
                                                        </span>
                                                    ) : null;
                                                })}
                                            </div>
                                        )}
                                    </td>
                                    <td className="w-[20%] py-4 px-4 text-sm text-foreground font-inter">
                                        {superAdmin ? (
                                            <button type="button" onClick={() => statusModalOpen(data)}
                                                className={`rounded-lg px-[15px] h-6 md:h-[30px] text-xs md:text-[15px] md:leading-6 font-medium font-inter pt-px flex items-center w-fit ${data.status === "Active"
                                                    ? "text-emerald-400 bg-emerald-400/10"
                                                    : "text-amber-400 bg-amber-400/10"
                                                    }`}
                                            >
                                                {data.status}
                                            </button>
                                        ) : (
                                            <span
                                                className={`rounded-lg px-[15px] h-6 md:h-[30px] text-xs md:text-[15px] md:leading-6 font-medium font-inter pt-px inline-flex items-center w-fit ${data.status === "Active"
                                                    ? "text-emerald-400 bg-emerald-400/10"
                                                    : "text-amber-400 bg-amber-400/10"
                                                    }`}
                                            >
                                                {data.status}
                                            </span>
                                        )}
                                    </td>
                                    <td className="w-[20%] py-4 px-4 text-sm text-foreground font-inter">
                                        {superAdmin ? (
                                            <RowActionsMenu
                                                actions={[
                                                    { label: "History", icon: HistoryIcon, onClick: () => handleHistoryClick(data) },
                                                    { label: "Roles", icon: Users, onClick: () => openRoleModal(data) },
                                                    { label: "Reset Password", icon: KeyRound, onClick: () => openResetModal(data) },
                                                    ...(data.status === "Active" ? [{
                                                        label: impersonatingCode === data.id ? "Logging in…" : "Login As",
                                                        icon: LogIn,
                                                        onClick: () => handleImpersonate(data),
                                                        disabled: impersonatingCode === data.id,
                                                    }] : []),
                                                    { label: "Deactivate", icon: UserX, onClick: () => deleteEmployee(data), destructive: true },
                                                ]}
                                            />
                                        ) : (
                                            <span className="text-muted-foreground text-xs font-inter">View only</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Box>
            {superAdmin && currentEmployee &&
                <StatusModal closeModal={closeStatusModal} employeeStatus={currentEmployee} onStatusUpdate={updateStatusText} />
            }
            {superAdmin && isEmployeeDelete &&
                <DeleteModal ref={deleteModalRef} closeButtonCLick={deleteModalClose}>
                    <h1 className="text-lg text-center font-inter font-semibold text-foreground border-b border-border p-5 mb-6">Deactivate Employee</h1>
                    <div className="flex flex-col gap-3 px-5 mb-5">
                        <p className="text-sm font-inter text-foreground">
                            Code: <span className="font-semibold text-foreground">{isEmployeeDelete.id}</span>
                        </p>
                        <p className="text-sm font-inter text-foreground">
                            Name: <span className="font-semibold text-foreground">{isEmployeeDelete.name}</span>
                        </p>
                        <p className="text-sm font-inter text-muted-foreground mt-2">
                            This will deactivate the employee. They will no longer appear in the active employees list.
                        </p>
                    </div>

                    <div className="border-t border-border py-4 px-5 flex justify-end gap-3">
                        <button onClick={deleteModalClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors" type="button">
                            Cancel
                        </button>
                        <button onClick={() => handleEmployeeDelete(isEmployeeDelete)} className="px-4 py-2 text-sm bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg font-inter transition-colors" type="button">
                            Deactivate
                        </button>
                    </div>
                </DeleteModal>
            }
            {superAdmin && isRoleModalOpen && selectedEmployeeForRole && (
                <RoleModal
                    employee={selectedEmployeeForRole}
                    onClose={closeRoleModal}
                    onRolesUpdated={() => {}}
                />
            )}
            {superAdmin && resetTarget && (
                <Modal ref={resetModalRef} size="sm" closeButtonCLick={closeResetModal} modalClassName="min-h-0">
                    <div className="px-6 py-5 border-b border-border">
                        <h2 className="text-base font-semibold text-foreground font-inter">Reset Password</h2>
                        <p className="text-xs text-muted-foreground font-inter mt-1">
                            {resetTarget.name} · {resetTarget.id}
                        </p>
                    </div>
                    <div className="px-6 py-5 space-y-4">
                        {resetSuccess ? (
                            <p className="text-sm text-emerald-400 font-inter text-center py-2">
                                Password reset successfully.
                            </p>
                        ) : (
                            <>
                                <div>
                                    <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">New password</label>
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={e => { setNewPassword(e.target.value); setResetError(""); }}
                                        placeholder="Min. 8 characters"
                                        className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Confirm password</label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={e => { setConfirmPassword(e.target.value); setResetError(""); }}
                                        placeholder="Re-enter password"
                                        className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                    />
                                </div>
                                {resetError && (
                                    <p className="text-xs text-destructive font-inter">{resetError}</p>
                                )}
                            </>
                        )}
                    </div>
                    <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
                        <Button disableDefaultStyles onClick={closeResetModal} buttonClasses="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors">
                            {resetSuccess ? "Close" : "Cancel"}
                        </Button>
                        {!resetSuccess && (
                            <Button
                                disableDefaultStyles
                                onClick={handleResetPassword}
                                disabled={resetSaving}
                                buttonClasses="px-4 py-2 text-sm bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground rounded-xl font-inter transition-colors"
                            >
                                {resetSaving ? <Spinner size="sm" className="text-primary-foreground" /> : "Reset Password"}
                            </Button>
                        )}
                    </div>
                </Modal>
            )}
        </>
    )
}

export default EmployeeTable
